/**
 * ヘッダー機能
 *
 * @package Spice_Theme
 */

import { gsap } from 'gsap';

const SUBMENU_STAGGER_SELECTOR =
  '.l-header__megamenu-service__left, .l-header__megamenu-service__pillar, ' +
  '.l-header__submenu-body > .c-button, .l-header__submenu-item';

function getClipEl(submenu) {
  return (
    submenu.querySelector('.l-header__submenu-body') ||
    submenu.firstElementChild ||
    submenu
  );
}

/**
 * ヘッダーインタラクションを管理するクラス
 */
class Header {
  constructor() {
    // 要素
    this.header = document.querySelector('.l-header');
    this.headerMain = document.querySelector('.l-header__main');
    this.menuBtn = document.querySelector('.l-header__menu-btn');
    this.backdrop = document.querySelector('.l-header-backdrop');
    this.menu = document.getElementById('header-menu');
    // モーションボタン（共通コンポーネント）
    this.motionBtns = document.querySelectorAll('.c-motion-btn');

    // メニューボタン内のテキストラベル
    this.menuBtnLabel = this.menuBtn?.querySelector(
      '.l-header__menu-btn-label--menu'
    );

    // SPメニュー要素
    this.spMenu = document.getElementById('sp-menu');
    this.spMenuCloseBtn = document.querySelector('.l-sp-menu__close-btn');

    // サブメニュー要素
    this.submenuTriggers = document.querySelectorAll(
      '.l-header__nav-link[data-submenu]'
    );

    // ブレイクポイント
    this.spBreakpoint = 768;

    // 状態
    this.isMenuOpen = false;
    this.isSpMenuOpen = false;
    this.isMenuAnimating = false;
    this.isMotionEnabled = true;
    this.isPC = window.innerWidth > this.spBreakpoint;
    this.activeSubmenu = null;
    this.activeTrigger = null;

    // フォーカス可能要素のセレクタ（フォーカストラップ用）
    this.focusableSelector =
      'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    // サブメニュー用タイマー（WCAG 2.1 1.4.13 Hoverable対応）
    this.submenuCloseTimer = null;
    this.submenuCloseDelay = 100; // 閉じるまでの遅延（ms）

    // サブメニュー GSAP タイムライン
    this._submenuOpenTl = null;
    this._submenuCloseTl = null;

    // セクション単位のヘッダー色制御
    this._darkSectionObserver = null;
    this._activeDarkSections = new Set();
    this._lightSectionObserver = null;
    this._activeLightSections = new Set();

    // GSAP ターゲット（メガメニュードロワーアニメーション）
    this.megaBg = null;
    this.megaBrand = null;
    this.megaCta = null;
    this.megaNavItems = [];
    this.megaSubItems = [];
    this.megaMedia = null;
    this.openTl = null;
    this.closeTl = null;

    // メソッドのバインド
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.openSpMenu = this.openSpMenu.bind(this);
    this.closeSpMenu = this.closeSpMenu.bind(this);
    this.toggleMotion = this.toggleMotion.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.closeAllSubmenus = this.closeAllSubmenus.bind(this);
    // ホバー/フォーカス用のメソッド
    this.handleSubmenuEnter = this.handleSubmenuEnter.bind(this);
    this.handleSubmenuLeave = this.handleSubmenuLeave.bind(this);
    this.handleSubmenuFocusIn = this.handleSubmenuFocusIn.bind(this);
    this.handleSubmenuFocusOut = this.handleSubmenuFocusOut.bind(this);

    // 初期化
    this.init();
  }

  /**
   * イベントリスナーの初期化
   */
  init() {
    // メニューボタン - 画面サイズに応じて異なるメニューを開く / トグル
    if (this.menuBtn) {
      this.menuBtn.addEventListener('click', () => {
        if (window.matchMedia('(hover: none)').matches) {
          this.menuBtn.classList.add('is-ring-playing');
        }
        if (window.innerWidth <= this.spBreakpoint) {
          if (this.isSpMenuOpen) {
            this.closeSpMenu();
          } else {
            this.openSpMenu();
          }
        } else {
          if (this.isMenuOpen) {
            this.closeMenu();
          } else {
            this.openMenu();
          }
        }
      });

      const ringCircle = this.menuBtn.querySelector(
        '.l-header__menu-btn-ring-circle'
      );
      ringCircle?.addEventListener('animationend', event => {
        if (event.animationName === 'header-btn-ring') {
          this.menuBtn.classList.remove('is-ring-playing');
          if (this.menuBtn.matches(':hover')) {
            this.menuBtn.classList.add('is-ring-done');
          } else {
            this.menuBtn.classList.add('is-ring-resetting');
          }
        } else if (event.animationName === 'header-btn-ring-reset') {
          this.menuBtn.classList.remove('is-ring-resetting');
        }
      });

      this.menuBtn.addEventListener('mouseleave', () => {
        if (this.menuBtn.classList.contains('is-ring-done')) {
          this.menuBtn.classList.remove('is-ring-done');
          this.menuBtn.classList.add('is-ring-resetting');
        }
      });
    }

    // SPメニュー閉じる
    if (this.spMenuCloseBtn) {
      this.spMenuCloseBtn.addEventListener('click', this.closeSpMenu);
    }

    // PCサブメニュー（ホバー/フォーカス対応 - WCAG 2.1 1.4.13準拠）
    this.submenuTriggers.forEach(trigger => {
      const submenuId = trigger.getAttribute('data-submenu');
      const submenu = document.getElementById(`submenu-${submenuId}`);

      // トリガーのホバー/フォーカスイベント
      trigger.addEventListener('mouseenter', this.handleSubmenuEnter);
      trigger.addEventListener('mouseleave', this.handleSubmenuLeave);
      trigger.addEventListener('focusin', this.handleSubmenuFocusIn);

      // サブメニューパネル自体のホバー/フォーカスイベント（Hoverable対応）
      if (submenu) {
        submenu.addEventListener('mouseenter', this.handleSubmenuEnter);
        submenu.addEventListener('mouseleave', this.handleSubmenuLeave);
        submenu.addEventListener('focusin', this.handleSubmenuFocusIn);
        submenu.addEventListener('focusout', this.handleSubmenuFocusOut);
      }
    });

    // ヘッダー外フォーカス移動で閉じる
    if (this.header) {
      this.header.addEventListener('focusout', this.handleSubmenuFocusOut);
    }

    // モーショントグル（共通コンポーネント）
    this.motionBtns.forEach(btn => {
      btn.addEventListener('click', this.toggleMotion);
    });

    // キーボードナビゲーション
    document.addEventListener('keydown', this.handleKeyDown);

    // リサイズ処理
    window.addEventListener('resize', this.handleResize);

    // 大画面でメニューを閉じる
    this.handleResize();

    // GSAP メガメニュー初期状態
    this._initMegaMenuGsap();

    // セクション単位のヘッダー色制御
    this._initHeaderSectionObserver();
  }

  /**
   * PCメニューを開く
   */
  openMenu() {
    if (!this.menu || this.isMenuAnimating) return;

    this.isMenuOpen = true;
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.menuBtn.setAttribute('aria-expanded', 'true');
    this.menuBtn.setAttribute('aria-label', 'メニューを閉じる');
    this.menuBtn.classList.add('is-open');
    if (this.menuBtnLabel) this.menuBtnLabel.textContent = 'CLOSE';
    document.body.classList.add('menu-open');

    // kill() 後に isMenuAnimating を設定（onInterrupt より後に立てる）
    if (this.closeTl) this.closeTl.kill();
    this.isMenuAnimating = true;
    this.openTl = this._buildMegaOpenTl(() => {
      this.isMenuAnimating = false;
    });

    // フォーカストラップ - 最初のフォーカス可能な要素にフォーカス
    const firstFocusable = this.menu.querySelector(this.focusableSelector);
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  /**
   * PCメニューを閉じる（PCメガメニュー）
   */
  closeMenu() {
    if (!this.menu || this.isMenuAnimating) return;

    this.menuBtn?.setAttribute('aria-expanded', 'false');

    // kill() 後に isMenuAnimating を設定（onInterrupt より後に立てる）
    if (this.openTl) this.openTl.kill();
    this.isMenuAnimating = true;
    this.closeTl = this._buildMegaCloseTl(() => {
      this.isMenuOpen = false;
      this.isMenuAnimating = false;
      this.menu.classList.remove('is-open');
      this.menu.setAttribute('aria-hidden', 'true');
      this.menuBtn?.classList.remove('is-open');
      this.menuBtn?.setAttribute('aria-label', 'メニューを開く');
      if (this.menuBtnLabel) this.menuBtnLabel.textContent = 'MENU';
      document.body.classList.remove('menu-open');
      if (this.menuBtn) this.menuBtn.focus();
    });
  }

  /**
   * SPモバイルメニューを開く
   */
  openSpMenu() {
    if (!this.spMenu) return;

    this.isSpMenuOpen = true;
    this.spMenu.classList.add('is-open');
    this.spMenu.setAttribute('aria-hidden', 'false');
    this.menuBtn.setAttribute('aria-expanded', 'true');
    this.menuBtn.classList.add('is-open');
    if (this.menuBtnLabel) this.menuBtnLabel.textContent = 'CLOSE';
    document.body.classList.add('menu-open');

    // フォーカストラップ - 最初のフォーカス可能な要素にフォーカス
    const firstFocusable = this.spMenu.querySelector(this.focusableSelector);
    if (firstFocusable) {
      firstFocusable.focus();
    }
  }

  /**
   * SPモバイルメニューを閉じる
   */
  closeSpMenu() {
    if (!this.spMenu) return;

    this.isSpMenuOpen = false;
    this.spMenu.classList.remove('is-open');
    this.spMenu.setAttribute('aria-hidden', 'true');
    this.menuBtn.setAttribute('aria-expanded', 'false');
    this.menuBtn.classList.remove('is-open');
    if (this.menuBtnLabel) this.menuBtnLabel.textContent = 'MENU';
    document.body.classList.remove('menu-open');

    // メニューボタンにフォーカスを戻す
    if (this.menuBtn) {
      this.menuBtn.focus();
    }
  }

  /**
   * すべてのPCサブメニューを閉じる
   */
  closeAllSubmenus() {
    this.clearSubmenuTimer();

    this._submenuOpenTl?.kill();
    this._submenuOpenTl = null;

    if (this._submenuCloseTl) return;

    const submenu = this.activeSubmenu;

    this.submenuTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
      const id = trigger.getAttribute('data-submenu');
      const s = document.getElementById(`submenu-${id}`);
      if (s && s !== submenu) {
        s.classList.remove('is-open');
        s.setAttribute('aria-hidden', 'true');
        gsap.set(getClipEl(s), { clearProps: 'clipPath' });
      }
    });

    this.activeSubmenu = null;
    this.activeTrigger = null;
    this.header?.classList.remove('l-header--submenu-open');
    this.menuBtn?.classList.remove('is-submenu-open');
    this.backdrop?.classList.remove('is-active');

    if (!submenu) return;

    submenu.setAttribute('aria-hidden', 'true');

    const closeClipEl = getClipEl(submenu);
    const imageEl = submenu.querySelector('.l-header__submenu-image');
    const descEl = submenu.querySelector('.l-header__megamenu-service__desc');
    const staggerItems = Array.from(
      submenu.querySelectorAll(SUBMENU_STAGGER_SELECTOR)
    );
    const fadeItems = [
      ...staggerItems,
      ...(imageEl ? [imageEl] : []),
      ...(descEl ? [descEl] : []),
    ];
    this._submenuCloseTl = gsap.timeline({
      onComplete: () => {
        submenu.classList.remove('is-open');
        gsap.set(closeClipEl, { clearProps: 'clipPath' });
        if (staggerItems.length)
          gsap.set(staggerItems, { clearProps: 'opacity,y' });
        if (imageEl) gsap.set(imageEl, { clearProps: 'opacity' });
        if (descEl) gsap.set(descEl, { clearProps: 'opacity,y' });
        submenu.querySelectorAll('[role="tabpanel"]').forEach(panel => {
          gsap.killTweensOf(panel);
          gsap.set(panel, { clearProps: 'opacity' });
        });
        this._submenuCloseTl = null;
      },
    });

    if (fadeItems.length) {
      this._submenuCloseTl.to(
        fadeItems,
        { opacity: 0, duration: 0.18, ease: 'power1.out' },
        0
      );
    }

    this._submenuCloseTl.to(
      closeClipEl,
      { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.42, ease: 'expo.in' },
      0.02
    );
  }

  /**
   * サブメニュー閉じるタイマーをクリア
   */
  clearSubmenuTimer() {
    if (this.submenuCloseTimer) {
      clearTimeout(this.submenuCloseTimer);
      this.submenuCloseTimer = null;
    }
  }

  /**
   * 特定のサブメニューを開く
   *
   * @param {Element} trigger - トリガー要素
   */
  openSubmenu(trigger) {
    const submenuId = trigger.getAttribute('data-submenu');
    const submenu = document.getElementById(`submenu-${submenuId}`);

    if (!submenu) return;

    this.clearSubmenuTimer();

    // 実行中のアニメーションをキャンセル
    this._submenuOpenTl?.kill();
    this._submenuOpenTl = null;
    this._submenuCloseTl?.kill();
    this._submenuCloseTl = null;

    // 他のサブメニューを即座に閉じる
    this.submenuTriggers.forEach(otherTrigger => {
      if (otherTrigger !== trigger) {
        otherTrigger.setAttribute('aria-expanded', 'false');
        const otherId = otherTrigger.getAttribute('data-submenu');
        const otherSubmenu = document.getElementById(`submenu-${otherId}`);
        if (otherSubmenu) {
          otherSubmenu.classList.remove('is-open');
          otherSubmenu.setAttribute('aria-hidden', 'true');
          gsap.set(getClipEl(otherSubmenu), { clearProps: 'clipPath' });
        }
      }
    });

    // 現在のサブメニューを開く
    trigger.setAttribute('aria-expanded', 'true');
    gsap.set(getClipEl(submenu), { clipPath: 'inset(100% 0% 0% 0%)' });
    submenu.classList.add('is-open');
    submenu.setAttribute('aria-hidden', 'false');
    this.activeSubmenu = submenu;
    this.activeTrigger = trigger;
    this.header?.classList.add('l-header--submenu-open');
    this.menuBtn?.classList.add('is-submenu-open');
    this.backdrop?.classList.add('is-active');

    if (submenu.classList.contains('l-header__submenu--service')) {
      submenu.dispatchEvent(new CustomEvent('megamenu-service:open'));
    }

    // オープンアニメーション
    const openClipEl = getClipEl(submenu);
    this._submenuOpenTl = gsap.timeline();
    this._submenuOpenTl.to(
      openClipEl,
      { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.55, ease: 'expo.out' },
      0
    );

    const imageEl = submenu.querySelector('.l-header__submenu-image');
    if (imageEl) {
      gsap.set(imageEl, { opacity: 0 });
      this._submenuOpenTl.to(
        imageEl,
        { opacity: 1, duration: 0.4, ease: 'power2.out' },
        0.25
      );
    }

    const openItems = Array.from(
      submenu.querySelectorAll(SUBMENU_STAGGER_SELECTOR)
    );
    if (openItems.length) {
      gsap.set(openItems, { opacity: 0, y: 10 });
      this._submenuOpenTl.to(
        openItems,
        { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out', stagger: 0.02 },
        0.2
      );
    }

    const descEl = submenu.querySelector('.l-header__megamenu-service__desc');
    if (descEl) {
      gsap.set(descEl, { opacity: 0, y: 10 });
      this._submenuOpenTl.to(
        descEl,
        { opacity: 1, y: 0, duration: 0.45, ease: 'power3.out' },
        0.2
      );
    }
  }

  /**
   * サブメニューを遅延して閉じる（WCAG 2.1 1.4.13 Hoverable対応）
   */
  scheduleSubmenuClose() {
    this.clearSubmenuTimer();
    this.submenuCloseTimer = setTimeout(() => {
      this.closeAllSubmenus();
    }, this.submenuCloseDelay);
  }

  /**
   * サブメニューへのマウス進入を処理
   *
   * @param {MouseEvent} event - マウスイベント
   */
  handleSubmenuEnter(event) {
    const target = event.currentTarget;

    // タイマーをキャンセル（ポインタがサブメニューに移動した場合）
    this.clearSubmenuTimer();

    // トリガーの場合はサブメニューを開く
    if (target.hasAttribute('data-submenu')) {
      this.openSubmenu(target);
    }
  }

  /**
   * サブメニューからのマウス離脱を処理
   */
  handleSubmenuLeave() {
    // 遅延して閉じる（ユーザーがサブメニューに移動する時間を確保）
    this.scheduleSubmenuClose();
  }

  /**
   * サブメニューへのフォーカス進入を処理
   *
   * @param {FocusEvent} event - フォーカスイベント
   */
  handleSubmenuFocusIn(event) {
    const target = event.currentTarget;

    // タイマーをキャンセル
    this.clearSubmenuTimer();

    // トリガーの場合はサブメニューを開く
    if (target.hasAttribute('data-submenu')) {
      this.openSubmenu(target);
    }
  }

  /**
   * サブメニューからのフォーカス離脱を処理
   *
   * @param {FocusEvent} event - フォーカスイベント
   */
  handleSubmenuFocusOut(event) {
    // 次のフォーカス先がサブメニュー内またはトリガー内かチェック
    const relatedTarget = event.relatedTarget;

    // 次のフォーカス先がない場合（ブラウザ外など）
    if (!relatedTarget) {
      this.scheduleSubmenuClose();
      return;
    }

    // 次のフォーカス先がサブメニュー内またはトリガー内かチェック
    const isInSubmenu =
      this.activeSubmenu && this.activeSubmenu.contains(relatedTarget);
    const isInTrigger =
      this.activeTrigger && this.activeTrigger.contains(relatedTarget);
    const isSubmenuTrigger = relatedTarget.hasAttribute('data-submenu');

    // 関連するトリガーへの移動かチェック
    const isRelatedTrigger =
      isSubmenuTrigger &&
      this.activeTrigger &&
      relatedTarget.getAttribute('data-submenu') ===
        this.activeTrigger.getAttribute('data-submenu');

    if (!isInSubmenu && !isInTrigger && !isRelatedTrigger) {
      // 別のサブメニュートリガーへの移動の場合はすぐに閉じない
      if (isSubmenuTrigger) {
        return;
      }
      this.scheduleSubmenuClose();
    }
  }

  /**
   * モーション設定をトグル
   */
  toggleMotion() {
    if (!this.motionBtns || this.motionBtns.length === 0) return;

    this.isMotionEnabled = !this.isMotionEnabled;

    // すべてのモーションボタンの状態を同期
    this.motionBtns.forEach(btn => {
      btn.setAttribute('aria-pressed', this.isMotionEnabled.toString());
    });

    // 視覚状態を更新（CSSで処理）
    // 他のコンポーネントがリッスンできるカスタムイベントをディスパッチ
    document.dispatchEvent(
      new CustomEvent('motionPreferenceChange', {
        detail: { enabled: this.isMotionEnabled },
      })
    );

    // 設定を保存
    try {
      localStorage.setItem('motionEnabled', this.isMotionEnabled.toString());
    } catch (e) {
      // localStorageが利用不可
      console.error(
        '[Header.toggleMotion] Failed to save motion preference to localStorage:',
        e
      );
    }
  }

  /**
   * キーボードナビゲーションを処理
   *
   * @param {KeyboardEvent} event - キーボードイベント
   */
  handleKeyDown(event) {
    // Escapeキーでメニューを閉じる（WCAG 2.1 1.4.13 Dismissible対応）
    if (event.key === 'Escape') {
      if (this.isMenuOpen) {
        this.closeMenu();
      }
      if (this.isSpMenuOpen) {
        this.closeSpMenu();
      }
      if (this.activeSubmenu) {
        // トリガーにフォーカスを戻す
        const triggerToFocus = this.activeTrigger;
        this.closeAllSubmenus();
        if (triggerToFocus) {
          triggerToFocus.focus();
        }
      }
    }

    // サブメニューが開いている状態でのTab操作
    // WCAG: パネル内にフォーカス移動できるようにする
    if (event.key === 'Tab' && this.activeSubmenu && this.activeTrigger) {
      const submenu = this.activeSubmenu;
      const trigger = this.activeTrigger;
      const focusableElements = submenu.querySelectorAll(
        this.focusableSelector
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      // トリガーにフォーカスがある場合、Tabでサブメニュー内の最初の要素に移動
      if (document.activeElement === trigger && !event.shiftKey) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      // サブメニュー内の最後の要素からTabで次のナビ項目に移動
      if (document.activeElement === lastElement && !event.shiftKey) {
        this.closeAllSubmenus();
        return;
      }

      // Shift+Tabでサブメニュー内の最初の要素からトリガーに戻る
      if (document.activeElement === firstElement && event.shiftKey) {
        event.preventDefault();
        trigger.focus();
        return;
      }
    }

    // PCメニューが開いている時のフォーカストラップ
    if (this.isMenuOpen && event.key === 'Tab') {
      const focusableElements = this.menu.querySelectorAll(
        this.focusableSelector
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    // SPメニューが開いている時のフォーカストラップ
    if (this.isSpMenuOpen && event.key === 'Tab') {
      const focusableElements = this.spMenu.querySelectorAll(
        this.focusableSelector
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  /**
   * ウィンドウリサイズを処理
   */
  handleResize() {
    this.isPC = window.innerWidth > this.spBreakpoint;

    // デスクトップにリサイズ時にSPメニューを閉じる
    if (this.isPC && this.isSpMenuOpen) {
      this.closeSpMenu();
    }

    // モバイルにリサイズ時にPCメガメニューを閉じる
    if (!this.isPC && this.isMenuOpen) {
      this.closeMenu();
    }

    // モバイルにリサイズ時にサブメニューを閉じる
    if (!this.isPC && this.activeSubmenu) {
      this.closeAllSubmenus();
    }

    // rootMargin は init 時の innerHeight / headerHeight から算出するため、
    // リサイズ後に再計算が必要。デバウンスで再生成する。
    clearTimeout(this._sectionObserverResizeTimer);
    this._sectionObserverResizeTimer = setTimeout(() => {
      this._darkSectionObserver?.disconnect();
      this._lightSectionObserver?.disconnect();
      this._activeDarkSections.clear();
      this._activeLightSections.clear();
      this._initHeaderSectionObserver();
    }, 200);
  }

  /**
   * localStorageからモーション設定を復元
   */
  restoreMotionPreference() {
    try {
      const stored = localStorage.getItem('motionEnabled');
      if (stored !== null) {
        this.isMotionEnabled = stored === 'true';
        // すべてのモーションボタンの状態を同期
        this.motionBtns.forEach(btn => {
          btn.setAttribute('aria-pressed', this.isMotionEnabled.toString());
        });
      }
    } catch (e) {
      // localStorageが利用不可
    }
  }

  /**
   * イベントリスナーを破棄
   */
  destroy() {
    if (this.menuBtn) {
      this.menuBtn.removeEventListener('click', this.openMenu);
      this.menuBtn.removeEventListener('click', this.openSpMenu);
    }

    if (this.spMenuCloseBtn) {
      this.spMenuCloseBtn.removeEventListener('click', this.closeSpMenu);
    }

    this.submenuTriggers.forEach(trigger => {
      const submenuId = trigger.getAttribute('data-submenu');
      const submenu = document.getElementById(`submenu-${submenuId}`);

      trigger.removeEventListener('mouseenter', this.handleSubmenuEnter);
      trigger.removeEventListener('mouseleave', this.handleSubmenuLeave);
      trigger.removeEventListener('focusin', this.handleSubmenuFocusIn);

      if (submenu) {
        submenu.removeEventListener('mouseenter', this.handleSubmenuEnter);
        submenu.removeEventListener('mouseleave', this.handleSubmenuLeave);
        submenu.removeEventListener('focusin', this.handleSubmenuFocusIn);
        submenu.removeEventListener('focusout', this.handleSubmenuFocusOut);
      }
    });

    if (this.header) {
      this.header.removeEventListener('focusout', this.handleSubmenuFocusOut);
    }

    // モーションボタン（共通コンポーネント）
    this.motionBtns.forEach(btn => {
      btn.removeEventListener('click', this.toggleMotion);
    });

    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('resize', this.handleResize);
    // GSAP タイムラインのクリーンアップ
    if (this.openTl) this.openTl.kill();
    if (this.closeTl) this.closeTl.kill();
    this._submenuOpenTl?.kill();
    this._submenuCloseTl?.kill();
    this._darkSectionObserver?.disconnect();
    this._lightSectionObserver?.disconnect();
  }

  _initHeaderSectionObserver() {
    const headerHeight = this.header?.offsetHeight ?? 100;
    // sectionBuffer: 次セクションをヘッダー帯に入る手前から "交差中" と判定し、
    // セクション間のマージン領域でのちらつきを防ぐ
    const sectionBuffer = headerHeight;
    const zoneBottom = Math.max(
      0,
      window.innerHeight - headerHeight - sectionBuffer
    );
    const margin = { rootMargin: `0px 0px -${zoneBottom}px 0px` };

    // dark/light をヘッダー帯 [0, headerHeight] との重複面積で排他的に解決する。
    // 2 つの Observer が独立にクラスをトグルすると、境界付近で両クラスが同時に付与され
    // CSS の記述順に結果が委ねられてしまうため、1 か所で勝者を決める。
    const resolveHeaderSection = () => {
      let dominantEl = null;
      let maxOverlap = -1;
      const checkSet = set => {
        for (const el of set) {
          const rect = el.getBoundingClientRect();
          const overlap = Math.max(
            0,
            Math.min(rect.bottom, headerHeight) - Math.max(rect.top, 0)
          );
          if (overlap > maxOverlap) {
            maxOverlap = overlap;
            dominantEl = el;
          }
        }
      };
      checkSet(this._activeDarkSections);
      checkSet(this._activeLightSections);

      const isDark =
        dominantEl !== null && this._activeDarkSections.has(dominantEl);
      const isLight =
        dominantEl !== null && this._activeLightSections.has(dominantEl);
      this.header?.classList.toggle('l-header--dark-section', isDark);
      this.menuBtn?.classList.toggle('is-dark-section', isDark);
      this.header?.classList.toggle('l-header--light-section', isLight);
      this.menuBtn?.classList.toggle('is-light-section', isLight);
    };

    const darkSections = document.querySelectorAll('[data-header-dark]');
    if (darkSections.length) {
      this._darkSectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._activeDarkSections.add(entry.target);
          } else {
            this._activeDarkSections.delete(entry.target);
          }
        });
        resolveHeaderSection();
      }, margin);
      darkSections.forEach(el => this._darkSectionObserver.observe(el));
    }

    const lightSections = document.querySelectorAll('[data-header-light]');
    if (lightSections.length) {
      this._lightSectionObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this._activeLightSections.add(entry.target);
          } else {
            this._activeLightSections.delete(entry.target);
          }
        });
        resolveHeaderSection();
      }, margin);
      lightSections.forEach(el => this._lightSectionObserver.observe(el));
    }
  }

  /**
   * GSAP メガメニュー初期状態を設定
   * （メニューが閉じている時の非表示状態）
   */
  _initMegaMenuGsap() {
    if (!this.menu) return;

    this.megaBg = this.menu.querySelector('.l-mega-menu__bg');
    this.megaBrand = this.menu.querySelector('.l-mega-menu__brand');
    this.megaCta = this.menu.querySelector('.l-mega-menu__cta');
    this.megaNavItems = Array.from(
      this.menu.querySelectorAll('.l-mega-menu__nav-item')
    );
    this.megaSubItems = Array.from(
      this.menu.querySelectorAll('.l-mega-menu__sub-item')
    );
    this.megaMedia = this.menu.querySelector('.l-mega-menu__media');

    if (this.megaBg)
      gsap.set(this.megaBg, { clipPath: 'inset(100% 0% 0% 0%)' });
    const leftFades = [this.megaBrand, this.megaCta].filter(Boolean);
    if (leftFades.length) gsap.set(leftFades, { opacity: 0 });
    if (this.megaNavItems.length)
      gsap.set(this.megaNavItems, { opacity: 0, y: 12 });
    if (this.megaSubItems.length)
      gsap.set(this.megaSubItems, { opacity: 0, y: 10 });
    if (this.megaMedia)
      gsap.set(this.megaMedia, {
        clipPath: 'inset(100% 0% 0% 0%)',
        opacity: 0,
      });
  }

  /**
   * GSAP メガメニュー オープンタイムラインを構築
   *
   * @param {Function} onComplete - アニメーション完了後に呼ぶコールバック
   * @returns {gsap.core.Timeline}
   */
  _buildMegaOpenTl(onComplete) {
    const tl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => onComplete?.(),
      onInterrupt: () => {
        this.isMenuAnimating = false;
      },
    });

    if (this.megaBg)
      tl.to(
        this.megaBg,
        { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'expo.out' },
        0
      );

    const leftFades = [this.megaBrand, this.megaCta].filter(Boolean);
    if (leftFades.length)
      tl.to(
        leftFades,
        { opacity: 1, duration: 0.32, ease: 'power1.out', stagger: 0.07 },
        0.28
      );

    if (this.megaNavItems.length)
      tl.to(
        this.megaNavItems,
        { opacity: 1, y: 0, duration: 0.52, ease: 'power3.out', stagger: 0.07 },
        0.34
      );

    if (this.megaSubItems.length)
      tl.to(
        this.megaSubItems,
        { opacity: 1, y: 0, duration: 0.46, ease: 'power3.out', stagger: 0.06 },
        0.46
      );

    if (this.megaMedia)
      tl.to(
        this.megaMedia,
        {
          clipPath: 'inset(0% 0% 0% 0%)',
          opacity: 1,
          duration: 0.75,
          ease: 'expo.out',
        },
        0.36
      );

    return tl;
  }

  /**
   * GSAP メガメニュー クローズタイムラインを構築
   *
   * @param {Function} onComplete - アニメーション完了後に呼ぶコールバック
   * @returns {gsap.core.Timeline}
   */
  _buildMegaCloseTl(onComplete) {
    const leftFades = [this.megaBrand, this.megaCta].filter(Boolean);
    const targets = [
      ...leftFades,
      ...this.megaNavItems,
      ...this.megaSubItems,
      this.megaMedia,
    ].filter(Boolean);

    const tl = gsap.timeline({
      defaults: { overwrite: 'auto' },
      onComplete: () => {
        this._initMegaMenuGsap();
        onComplete?.();
      },
      onInterrupt: () => {
        this.isMenuAnimating = false;
      },
    });

    if (targets.length)
      tl.to(targets, { opacity: 0, duration: 0.22, ease: 'power1.out' }, 0);

    if (this.megaBg)
      tl.to(
        this.megaBg,
        { clipPath: 'inset(0% 0% 100% 0%)', duration: 0.6, ease: 'expo.in' },
        0.02
      );

    return tl;
  }
}

// DOMの準備ができたらヘッダーを初期化
document.addEventListener('DOMContentLoaded', () => {
  const header = new Header();
  header.restoreMotionPreference();
});

export default Header;
